import { useVirtualizer } from '@tanstack/react-virtual';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionStorage } from 'react-use';

import { TableHeadCell, useFilterableTable, useSearchableTable } from '@ifs-gcp/components';
import type { IChecklistNode } from '@ifs-gcp/core';
import type {
  ChecklistChapterChild,
  ChecklistRequirementChild,
  FilterTypes,
} from '@ifs-gcp/core/models';

import { useIndexBasedScrollRestoration } from '@/hooks/useIndexBasedScrollRestoration';
import { useAuditStore } from '@/store/auditStore';
import { useRouterStore } from '@/store/routerStore';
import { syncReportLanguage } from '@/utils/syncReportLanguage';
import { getTranslation } from '@/utils/translations';

import { ChecklistHeader } from './checklist-header';
import { ChecklistTable } from './checklist-table';
import styles from './checklist.module.scss';
import { ChapterRow, RequirementRow } from './rows';
import { useChecklistValidationSwitch } from './useChecklistValidationSwitch';
import { mapFilterId, processColumnToFilter } from './utils';

export type DropdownItem<T> = {
  label: string;
  value: T;
  isActive?: boolean;
  disabled?: boolean;
};

type BaseFilterableTableColumn = {
  id: string;
  minWidth?: number;
  width?: 'fill' | number;
  label: string;
  disabled?: boolean;
  filterOptionId?: FilterTypes | null;
  filters?: DropdownItem<string>[];
  isColumnFiltered: boolean;
};

function buildUuidToIndexMap(
  treeData: (ChecklistChapterChild | ChecklistRequirementChild)[],
): Map<string, number> {
  const map = new Map<string, number>();
  let currentIndex = 0;

  const walk = (nodes: (ChecklistChapterChild | ChecklistRequirementChild)[]) => {
    for (const node of nodes) {
      map.set(node.uuId, currentIndex++);

      if ('children' in node && node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(treeData);
  return map;
}

const flattenChecklist = (
  tree:
    | (ChecklistChapterChild | ChecklistRequirementChild)
    | (ChecklistChapterChild | ChecklistRequirementChild)[],
): (ChecklistChapterChild | ChecklistRequirementChild)[] => {
  if (Array.isArray(tree)) {
    return tree.flatMap((child) => [{ ...child, children: [] }, ...flattenChecklist(child)]);
  }

  if (!tree || !('children' in tree) || tree.children.length === 0) {
    return [];
  }

  return tree.children.flatMap((child) => [{ ...child, children: [] }, ...flattenChecklist(child)]);
};

type ChecklistProps = {
  element: IChecklistNode;
};

export const Checklist = observer(({ element }: ChecklistProps) => {
  const audit = useAuditStore((state) => state.audit)!;
  const scrollTo = useRouterStore((state) => state.scrollTo);
  const resetScrollTo = useRouterStore((state) => state.resetScrollTo);

  const domain = useLocalObservable(() => audit.factory('checklist', element));

  const {
    props: {
      name,
      id: checklistId,
      context: checklistContext,
      supportLanguage,
      chaptersList,
      availableScores,
      translations,
      columnSetting: { columns: columnDefinitions },
    },
  } = element;

  const [isValidationEnabled, setIsValidationEnabled] = useChecklistValidationSwitch(checklistId);
  const [isErrorsOnlyVisibility, setIsErrorsOnlyVisibility] = useSessionStorage(
    `axpxApp_isErrorsOnlyVisibility_${checklistId}`,
    false,
  );

  const enableScrollRestoration = useRef(!scrollTo);

  const [uuidsToIndexMap] = useState(buildUuidToIndexMap(element.props.children));

  const initialColumns = useMemo(() => {
    const hasActiveVisibilityFilter = Object.values(columnDefinitions).some(
      (column) => column.defaultVisibility !== true,
    );

    return columnDefinitions.reduce<Record<string, BaseFilterableTableColumn>>((acc, curr) => {
      if (
        !domain.isAdjustmentMode &&
        ['requirementExplanationPrevious', 'requirementScorePrevious'].includes(curr.id)
      ) {
        return acc;
      }

      return {
        ...acc,
        [curr.id]: {
          id: curr.id,
          disabled: curr.disableHide,
          isColumnFiltered: hasActiveVisibilityFilter ? curr.defaultVisibility : false,
          label: getTranslation(curr.header.text, domain.language),
          minWidth: curr.minWidth,
          width: curr.width,
          filterOptionId: curr.header.filterOptionId,
          filters: mapFilterId(
            translations,
            curr.header.filterOptionId,
            availableScores,
            domain.language,
          ).reduce<DropdownItem<string>[]>(
            (filterAcc, filter) => [
              ...filterAcc,
              {
                value: filter.value.toString(),
                label: filter.label,
                isActive: filter.isActive,
              },
            ],
            [],
          ),
        },
      };
    }, {});
  }, [columnDefinitions, translations, domain.isAdjustmentMode, domain.language, availableScores]);

  const { columns, visibleColumns, handleChangeFilter, handleResetFilter } = useFilterableTable({
    storageKey: checklistId,
    columns: initialColumns,
  });

  const handleSearchTemplate = (text: string) => {
    return audit.search(text, domain.language, ['checklist']);
  };

  const { searchText, searchResults, handleSetSearch } = useSearchableTable({
    language: domain.language,
    searchCallback: (text: string) =>
      handleSearchTemplate(text).filter(({ field }) => field.props.id === checklistId)?.[0]
        ?.results || [],
  });

  const cachedHandleSetSearch = useCallback((text: string) => {
    handleSetSearch(text);
  }, []);

  const checklistError = isValidationEnabled ? audit.status.getErrorByTemplate(element) : undefined;

  const flattenedTemplate = useMemo(() => {
    const checklist = flattenChecklist(domain.template.props.children);

    return checklist.reduce((acc, curr) => {
      acc.set(curr.uuId, curr);

      return acc;
    }, new Map<string, ChecklistRequirementChild | ChecklistChapterChild>());
  }, [domain.template.props.children]);

  const filteredRows = useMemo(() => {
    return Array.from(flattenedTemplate.values()).filter((templateRow) => {
      /**
       * Chapters are never filtered and always visible
       */
      if (templateRow.type === 'chapter') {
        return true;
      }

      if (isErrorsOnlyVisibility) {
        const rowError = checklistError?.errors.some(({ id }) => id === templateRow.uuId);

        if (!rowError) {
          return false;
        }
      }

      const row = domain.rows.get(templateRow.uuId);

      if (!row) return false;

      return Object.values(columns).every(processColumnToFilter(row));
    });
  }, [columns, domain.rows, isErrorsOnlyVisibility, checklistError?.errors]);

  const tableRef = useRef<HTMLDivElement | null>(null);

  const getItemKey = useCallback((index: number) => filteredRows[index].uuId, [filteredRows]);

  const rowVirtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: filteredRows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => 100,
    overscan: 10,
    getItemKey,
    enabled: Boolean(filteredRows.length),
  });

  useIndexBasedScrollRestoration(
    `checklist_${checklistId}`,
    rowVirtualizer,
    enableScrollRestoration.current,
  );

  const columnOptions = useMemo(
    () =>
      Object.values(columns)
        .filter(({ disabled }) => !disabled)
        .map(({ id, label, isColumnFiltered }) => ({
          value: id,
          label,
          isActive: isColumnFiltered,
        })),
    [columns],
  );

  const handleScrollToRow = useCallback(
    (rowUuid: string) => {
      const scrollIndex = uuidsToIndexMap.get(rowUuid);

      if (typeof scrollIndex === 'number') {
        rowVirtualizer.scrollToIndex(scrollIndex, { align: 'start' });
      }
    },
    [rowVirtualizer, uuidsToIndexMap],
  );

  const handleLanguageChange = useCallback(
    (language: string) => {
      domain.language = language;
      syncReportLanguage(audit, checklistContext, language);
    },
    [domain, audit, checklistContext],
  );

  useEffect(() => {
    const scrollIndex = scrollTo && uuidsToIndexMap.get(scrollTo);

    if (typeof scrollIndex === 'number') {
      setTimeout(() => {
        rowVirtualizer.scrollToIndex(scrollIndex, { align: 'start' });
      });

      resetScrollTo();
    }
  });

  useEffect(() => {
    return () => {
      domain.dispose();
    };
  }, []);

  return (
    <div className={styles.container}>
      <ChecklistHeader
        name={name}
        chapters={chaptersList}
        language={domain.language}
        supportedLanguages={supportLanguage}
        isValidationEnabled={isValidationEnabled}
        isErrorsOnlyVisibility={isErrorsOnlyVisibility}
        columnOptions={columnOptions}
        searchResults={searchResults}
        searchText={searchText}
        handleSetSearch={cachedHandleSetSearch}
        setIsValidationEnabled={setIsValidationEnabled}
        setIsErrorsOnlyVisibility={setIsErrorsOnlyVisibility}
        handleScrollToRow={handleScrollToRow}
        handleLanguageChange={handleLanguageChange}
      />

      <ChecklistTable
        head={
          <>
            {Object.values(visibleColumns).map(
              ({ id: columnId, minWidth, width, label, filters }) => (
                <TableHeadCell
                  key={columnId}
                  size="small"
                  width={width}
                  minWidth={minWidth}
                  filterLabel={label}
                  filters={filters}
                  onFilterReset={() => handleResetFilter(columnId)}
                  onFilterChange={(filterId) => handleChangeFilter(columnId, filterId)}
                  align={columnId === 'score' ? 'center' : 'left'}
                >
                  {label}
                </TableHeadCell>
              ),
            )}
          </>
        }
        height={rowVirtualizer.options.enabled ? rowVirtualizer.getTotalSize() : undefined}
        wrapperRef={tableRef}
      >
        {rowVirtualizer.getVirtualItems().map((item) => {
          const template = flattenedTemplate.get(item.key as string);

          if (!template) {
            return null;
          }

          if (template.type === 'requirement') {
            const row = domain.rows.get(item.key as string)!;

            return (
              <RequirementRow
                key={template.uuId}
                row={row}
                domain={domain}
                visibleColumns={visibleColumns}
                language={domain.language}
                activeColumnIds={Object.keys(visibleColumns)}
                rowVirtualizer={rowVirtualizer}
                virtualItem={item}
                searchText={searchText}
                error={checklistError?.errors.filter(({ id }) => id === template.uuId)}
              />
            );
          }

          return (
            <ChapterRow
              key={template.uuId}
              row={template}
              virtualItem={item}
              visibleColumns={visibleColumns}
              rowVirtualizer={rowVirtualizer}
              searchText={searchText}
              language={domain.language}
              activeColumnIds={Object.keys(visibleColumns)}
            />
          );
        })}
      </ChecklistTable>
    </div>
  );
});
